import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { auth, db } from '../lib/supabase';
import { setUser as setSentryUser } from '../lib/sentry';
import { identifyUser, trackEvent, trackSignUpOnce } from '../lib/analytics';
import { applyTrialIfCampaignPending } from '../lib/trial';
import { decodeNPI } from '../lib/npi';
import { getPersistedUTM, persistUTM } from '../lib/utm';
import {
  GOOGLE_IOS_CLIENT_ID,
  createNonce,
  nativeGoogleIdToken,
  signInWithGoogleIdToken,
} from '../lib/google-auth';
import { getPlatform } from '../lib/native/platform';
import type { User, SubscriptionTier } from '../types';
import { browserTimeZone } from '../lib/dates';

// Capture the `?ref=<encoded-npi>` from outreach links and persist as
// users.referrer_npi. Called after signup completes (email/password and OAuth).
// Idempotent — silently no-ops if no ref, no secret, or invalid token.
async function captureReferrerNPIIfPending(userId: string): Promise<void> {
  const utm = getPersistedUTM();
  if (!utm.ref) return;
  const secret = import.meta.env.VITE_REFERRAL_SECRET as string | undefined;
  if (!secret) {
    console.warn('VITE_REFERRAL_SECRET not set; skipping referrer_npi capture');
    return;
  }
  try {
    const npi = await decodeNPI(utm.ref, secret);
    if (npi) {
      await db.users.update(userId, { referrer_npi: npi });
    }
  } catch (err) {
    console.warn('captureReferrerNPIIfPending failed:', err);
  }
  // Clear ref from sessionStorage so a second account on the same browser
  // doesn't pick it up.
  const { ref: _drop, ...rest } = utm;
  persistUTM(rest);
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
  /**
   * True once initialize() has completed at least once (either by determining
   * the user is authenticated or by determining there's no session). Auth
   * guards use this to distinguish app-startup loading (show spinner) from
   * per-operation loading like signIn (don't blank the form). Without this,
   * the loading spinner would replace the Login form mid-submission, causing
   * Login to remount and its mount-effect to wipe any error before the user
   * could see it.
   */
  hasInitialized: boolean;
  isAuthenticated: boolean;
  error: string | null;

  // Actions
  initialize: () => Promise<void>;
  signUp: (email: string, password: string, fullName: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  // ID-token sign-in (Google's own button / native SDK). Resolves signed in.
  completeGoogleSignIn: (idToken: string, rawNonce: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateProfile: (updates: Partial<User>) => Promise<void>;
  refreshUser: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: true,
      hasInitialized: false,
      isAuthenticated: false,
      error: null,

      initialize: async () => {
        set({ isLoading: true, error: null });
        try {
          const session = await auth.getSession();
          if (session?.user) {
            // Get or create user profile in database
            let userProfile = await db.users.tryGet(session.user.id);
            if (!userProfile) {
              // User profile doesn't exist, try to create it
              try {
                userProfile = await db.users.create({
                  id: session.user.id,
                  email: session.user.email!,
                  full_name: session.user.user_metadata?.full_name || 'User',
                  role: 'learner',
                  subscription_tier: 'free' as SubscriptionTier,
                  xp: 0,
                  level: 1,
                  streak_days: 0,
                  longest_streak: 0,
                  questions_answered: 0,
                  questions_correct: 0,
                  questions_answered_today: 0,
                  daily_goal: 10,
                  notifications_enabled: true,
                  public_profile: false,
                  streak_freezes_available: 2,
                  streak_freezes_used_this_month: 0,
                  is_streak_frozen: false,
                });
              } catch (createError) {
                // If we can't create the profile, sign out and let user try again
                console.error('Failed to create user profile:', createError);
                await auth.signOut();
                set({
                  user: null,
                  isAuthenticated: false,
                  isLoading: false,
                  hasInitialized: true,
                });
                return;
              }
            }
            setSentryUser({ id: userProfile.id, email: userProfile.email, role: userProfile.role });
            identifyUser(userProfile.id, {
              role: userProfile.role,
              subscription_tier: userProfile.subscription_tier,
            });
            // OAuth signups never pass through the signUp action, so first
            // auth is detected here: a profile created in the last 5 minutes
            // is a new account. trackSignUpOnce dedupes against the email
            // path and against later reloads inside the window.
            if (
              userProfile.created_at &&
              Date.now() - new Date(userProfile.created_at).getTime() < 5 * 60 * 1000
            ) {
              trackSignUpOnce(
                userProfile.id,
                session.user.app_metadata?.provider ?? 'unknown'
              );
            }

            // Apply campaign trial + capture referrer NPI on first auth (e.g.
            // after Google OAuth round-trip). Both helpers are idempotent.
            // Not gated on trial_started_at: since Complimentary Pro (migration 75) every new
            // account has a trial set at insert, which skipped this and lost ?campaign=
            // attribution on Google signups. The RPC records the campaign (migration 80) and is
            // a no-op when there is no pending campaign.
            await applyTrialIfCampaignPending(userProfile.id);
            if (!userProfile.referrer_npi) {
              await captureReferrerNPIIfPending(userProfile.id);
            }

            // Keep the server's "today" in the learner's own time zone (migration 81).
            const tz = browserTimeZone();
            if (tz && userProfile.timezone !== tz) {
              // Best effort: never let this block sign-in.
              Promise.resolve()
                .then(() => db.sessions_rpc.setTimezone(tz))
                .catch((e) => console.warn('set_my_timezone failed:', e));
            }

            // Re-fetch in case the helpers updated the row, so the store reflects truth.
            try {
              const refreshed = await db.users.get(userProfile.id);
              userProfile = refreshed;
            } catch {
              // best-effort; the original profile is still valid
            }

            set({
              user: userProfile,
              isAuthenticated: true,
              isLoading: false,
              hasInitialized: true,
            });
          } else {
            setSentryUser(null);
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              hasInitialized: true,
            });
          }
        } catch (error) {
          // Don't show error for initialization failures - just treat as not authenticated
          console.error('Auth initialization error:', error);
          setSentryUser(null);
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            hasInitialized: true,
          });
        }
      },

      signUp: async (email, password, fullName) => {
        set({ isLoading: true, error: null });
        try {
          const { user: authUser } = await auth.signUp(email, password, fullName);
          if (authUser) {
            // Get-or-create the profile row. Prod has a handle_new_user trigger
            // that auto-creates public.users on signup, so an unconditional
            // INSERT here hit a duplicate PK (409). Prefer the existing row
            // (it respects trigger-set values like an invited 'creator' role);
            // only create when the trigger didn't.
            let newUser = await db.users.tryGet(authUser.id);
            if (!newUser) {
              newUser = await db.users.create({
                id: authUser.id,
                email: authUser.email!,
                full_name: fullName,
                role: 'learner',
                subscription_tier: 'free' as SubscriptionTier,
                xp: 0,
                level: 1,
                streak_days: 0,
                longest_streak: 0,
                questions_answered: 0,
                questions_correct: 0,
                questions_answered_today: 0,
                daily_goal: 10,
                notifications_enabled: true,
                public_profile: false,
                streak_freezes_available: 2,
                streak_freezes_used_this_month: 0,
                is_streak_frozen: false,
              });
            }

            // Apply campaign trial + capture referrer NPI from outreach link.
            // Both are idempotent and silently no-op when not applicable.
            await applyTrialIfCampaignPending(newUser.id);
            await captureReferrerNPIIfPending(newUser.id);

            // Re-fetch so the store reflects any trial/referrer updates.
            let finalUser = newUser;
            try {
              finalUser = await db.users.get(newUser.id);
            } catch {
              // best-effort
            }

            trackSignUpOnce(finalUser.id, 'email');
            identifyUser(finalUser.id, {
              role: finalUser.role,
              subscription_tier: finalUser.subscription_tier,
            });

            set({
              user: finalUser,
              isAuthenticated: true,
              isLoading: false,
            });
          }
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Sign up failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      signIn: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const signInResult = await auth.signIn(email, password);
          const authUser = signInResult.user;
          const session = signInResult.session;

          if (authUser) {
            let userProfile = await db.users.tryGet(authUser.id);
            if (!userProfile) {
              // User profile doesn't exist, create it
              console.log('User profile not found, creating...');
              try {
                userProfile = await db.users.create({
                  id: authUser.id,
                  email: authUser.email!,
                  full_name: authUser.user_metadata?.full_name || 'User',
                  role: 'learner',
                  subscription_tier: 'free' as SubscriptionTier,
                  xp: 0,
                  level: 1,
                  streak_days: 0,
                  longest_streak: 0,
                  questions_answered: 0,
                  questions_correct: 0,
                  questions_answered_today: 0,
                  daily_goal: 10,
                  notifications_enabled: true,
                  public_profile: false,
                  streak_freezes_available: 2,
                  streak_freezes_used_this_month: 0,
                  is_streak_frozen: false,
                });
              } catch (createError) {
                console.error('Failed to create user profile:', createError);
                const msg = createError instanceof Error ? createError.message : 'Failed to create user profile';
                set({ error: msg, isLoading: false });
                throw createError;
              }
            }
            trackEvent('login', { method: 'email' });
            identifyUser(userProfile.id, {
              role: userProfile.role,
              subscription_tier: userProfile.subscription_tier,
            });

            set({
              user: userProfile,
              isAuthenticated: true,
              isLoading: false,
            });
          }
        } catch (error: unknown) {
          let message = 'Sign in failed';
          if (error instanceof Error) {
            if (error.message === 'Failed to fetch' || error.message.includes('NetworkError')) {
              message = 'Unable to connect to the server. Please check your internet connection and try again.';
            } else if (error.message.includes('Invalid login credentials')) {
              message = 'Invalid email or password. Please try again.';
            } else {
              message = error.message;
            }
          }
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      signInWithGoogle: async () => {
        set({ isLoading: true, error: null });
        try {
          // iOS with a configured client ID: native Google sheet, no web redirect.
          if (getPlatform() === 'ios' && GOOGLE_IOS_CLIENT_ID) {
            const nonce = await createNonce();
            let idToken: string;
            try {
              idToken = await nativeGoogleIdToken(nonce.hashed);
            } catch (err) {
              // Closing Google's sheet is a choice, not an error.
              if (err instanceof Error && /cancel/i.test(err.message)) {
                set({ isLoading: false });
                return;
              }
              throw err;
            }
            await get().completeGoogleSignIn(idToken, nonce.raw);
            return;
          }
          await auth.signInWithGoogle();
          // The redirect will handle the rest
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Google sign in failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      completeGoogleSignIn: async (idToken, rawNonce) => {
        set({ isLoading: true, error: null });
        try {
          await signInWithGoogleIdToken(idToken, rawNonce);
          // Same as the OAuth callback page: load (or create) the profile row.
          await get().initialize();
          if (!get().isAuthenticated) throw new Error('Google sign in failed');
          trackEvent('login', { method: 'google' });
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Google sign in failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      signOut: async () => {
        set({ isLoading: true, error: null });
        try {
          await auth.signOut();
          setSentryUser(null);
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Sign out failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      resetPassword: async (email) => {
        set({ isLoading: true, error: null });
        try {
          await auth.resetPassword(email);
          set({ isLoading: false });
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Password reset failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      updateProfile: async (updates) => {
        const { user } = get();
        if (!user) {
          set({ error: 'No user logged in' });
          return;
        }

        set({ isLoading: true, error: null });
        try {
          const updatedUser = await db.users.update(user.id, updates);
          set({
            user: updatedUser,
            isLoading: false,
          });
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Profile update failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      refreshUser: async () => {
        const { user } = get();
        if (!user) return;

        try {
          const refreshedUser = await db.users.get(user.id);
          set({ user: refreshedUser });
        } catch (error) {
          console.error('Failed to refresh user:', error);
        }
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'pulsemd-auth',
      partialize: (state) => ({
        // Only persist essential data, re-verify on init
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Helper hooks
export function useUser() {
  return useAuthStore((state) => state.user);
}

export function useIsAuthenticated() {
  return useAuthStore((state) => state.isAuthenticated);
}

export function useAuthLoading() {
  return useAuthStore((state) => state.isLoading);
}

export default useAuthStore;
